// The quiet that ends a listen, judged by a model when it can be — T451c (Natanel, 25 Oct 2026).
//
// Both engines (speech.ts, the browser's; transcribe.ts, Amazon Transcribe's) end a listen after a
// fixed silence: `SILENCE_MS` for a dictation, voice mode's `VOICE_SILENCE_MS` for the backup Talk
// (those two numbers stay, as the backup). When the app gives the engine a `finished` question —
// the BFF's `POST /speech/finished`, a small model's read of the words so far — the same watcher
// asks it a moment after the words stop changing and lets the answer move the end:
//
//   dictation (fills a box the person edits before Ask): finished → stop at once; not finished →
//     stay open to `DICTATION_CEILING_MS` of quiet; no answer by `silenceMs`, or null → stop there.
//   talk (a heard "yes" confirms a card): no read at all. Measured 25 Oct 2026, the model's answer
//     takes about a second, longer than the 0.9 s turn it could only lengthen, so it would arrive
//     after the turn had ended; Natanel kept the fixed `VOICE_SILENCE_MS`.
//
// An answer only counts for the words it was asked about: the words are compared with case,
// punctuation and spacing removed, so a result turning final with the same words is no change and
// asks nothing again, while a changed word voids a pending answer.

/** Asks whether the words so far are a finished thought; null when it cannot say. Never throws. */
export type FinishedAsk = (text: string, mode: ListenMode) => Promise<boolean | null>;

/** What the words are for: a dictation into the composer, or the backup Talk's spoken turn. */
export type ListenMode = 'dictation' | 'talk';

/** A dictation the model calls unfinished stays open to this much quiet. */
export const DICTATION_CEILING_MS = 4_000;
/** When a dictation's words are put to the model after they stop changing. */
export const DICTATION_ASK_AFTER_MS = 800;

/** The words alone: case, punctuation and whitespace removed. */
export const normalisedWords = (text: string): string =>
  text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

export interface SilenceWatcher {
  /** The engine's transcript so far, called on every result. */
  heard(text: string): void;
  /** Cancel every timer and void a pending answer: the listen is stopping or over. */
  clear(): void;
}

export function silenceWatcher(options: {
  /** Today's fixed silence for this listen. */
  silenceMs: number;
  mode: ListenMode;
  /** Absent: the fixed silence alone, exactly as before. */
  finished?: FinishedAsk | undefined;
  stop(): void;
}): SilenceWatcher {
  const { silenceMs, mode } = options;
  // A spoken turn is never asked about: its fixed silence is the whole rule.
  const finished = mode === 'dictation' ? options.finished : undefined;
  let timers: ReturnType<typeof setTimeout>[] = [];
  let words: string | null = null;
  let turn = 0;

  const clear = () => {
    turn += 1;
    for (const timer of timers) clearTimeout(timer);
    timers = [];
  };

  // Once: the first timer to fire ends the listen and cancels the rest.
  const stop = () => {
    clear();
    options.stop();
  };

  return {
    clear,
    heard(text) {
      if (!finished) {
        // Today's rule: every result restarts the one silence.
        clear();
        timers.push(setTimeout(stop, silenceMs));
        return;
      }
      const next = normalisedWords(text);
      if (next === words) return;
      words = next;
      clear();
      const mine = turn;
      // Set when the model says the words stop mid-thought: the base silence no longer ends it.
      let more = false;
      timers.push(
        setTimeout(() => {
          if (!more) stop();
        }, silenceMs),
      );
      if (next === '') return;
      // The stall limit: an unfinished turn still ends, at the ceiling.
      timers.push(setTimeout(stop, Math.max(DICTATION_CEILING_MS, silenceMs)));
      timers.push(
        setTimeout(() => {
          void Promise.resolve()
            .then(() => finished(text, mode))
            .then(
              (answer) => {
                if (mine !== turn) return;
                if (answer === false) more = true;
                else if (answer === true && mode === 'dictation') stop();
              },
              () => {},
            );
        }, DICTATION_ASK_AFTER_MS),
      );
    },
  };
}
